/**
 * Local-First Queue (MVP)
 * -----------------------------------------------------------------------------
 * A tiny, framework-agnostic offline write queue used by lightweight wellness
 * flows in HealthLoop (Daily Check-Ins, hydration/mood, reminders).
 *
 * Why this exists:
 *   The previous behaviour for these flows was "POST and pray" — if the user
 *   submitted a check-in on a flaky tube connection, the request would 5xx
 *   and the entry was simply lost. This module gives every flow a single,
 *   honest contract:
 *      1. Persist the user's intent locally the moment they tap submit.
 *      2. Try to deliver it to the backend immediately if we're online.
 *      3. If delivery fails (offline, 5xx, auth refresh in flight, etc.),
 *         park the payload in a per-queue localStorage list and retry the
 *         next time the browser fires an `online` event OR the next time
 *         the user manually taps refresh / opens the screen.
 *
 * Deliberate constraints:
 *   - No backend additions. Same endpoints, same payloads.
 *   - No fake "sync in progress" indicators. We only show truthful states:
 *       ONLINE_SYNCED | OFFLINE | SYNC_PAUSED | QUEUED | FLUSHING
 *   - Strict allow-list of flows: low-risk wellness data only. Prescriptions,
 *     clinical records, payments, etc. MUST NOT be wired up here.
 *   - Per-queue cap (default 25) so localStorage never blows up — older
 *     entries are dropped FIFO with a console warn so the bug is visible.
 *   - Each enqueued entry carries a v1 schema + an idempotency `clientId`
 *     so future-us can add deduplication on the server without breaking
 *     anything currently queued.
 *
 * Public surface (one factory, returns a queue handle):
 *
 *   const checkinQueue = createLocalFirstQueue({
 *     name: 'checkins',           // becomes localStorage key suffix
 *     sync: async (payload) => { ... }, // async fn returning Promise on POST
 *     maxSize: 25,                // optional, default 25
 *   });
 *
 *   await checkinQueue.enqueueAndSync(payload); // tries now, falls back to queue
 *   await checkinQueue.flush();                  // manual flush attempt
 *   checkinQueue.size();                         // current pending count
 *   checkinQueue.subscribe(listener);            // react to state changes
 *   checkinQueue.getStatus();                    // current STATUS value
 */

export const QUEUE_STATUS = Object.freeze({
  ONLINE_SYNCED: 'online_synced',
  OFFLINE: 'offline',
  QUEUED: 'queued',
  FLUSHING: 'flushing',
  SYNC_PAUSED: 'sync_paused',
});

const STORAGE_PREFIX = 'healthloop.queue.';

/**
 * Tiny global online/offline observer. We register listeners once for the
 * lifetime of the page so that every queue created by createLocalFirstQueue
 * shares the same source of truth — no duplicate listeners, no leaks.
 */
const globalOnlineListeners = new Set();
let globalOnlineWired = false;
function ensureGlobalOnlineWired() {
  if (globalOnlineWired || typeof window === 'undefined') return;
  globalOnlineWired = true;
  const fire = () => globalOnlineListeners.forEach((fn) => { try { fn(navigator.onLine !== false); } catch (_) {} });
  window.addEventListener('online', fire);
  window.addEventListener('offline', fire);
}

export function isOnline() {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

function safeRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
}

function safeWrite(key, arr) {
  try { localStorage.setItem(key, JSON.stringify(arr)); } catch (_) {}
}

function generateClientId() {
  // Lightweight UUID-ish — collision odds at our usage volume are nil and we
  // avoid pulling in a uuid lib for one tiny use site.
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createLocalFirstQueue({ name, sync, maxSize = 25, allowDedupeKey = null }) {
  if (!name || typeof name !== 'string') {
    throw new Error('createLocalFirstQueue: `name` is required');
  }
  if (typeof sync !== 'function') {
    throw new Error('createLocalFirstQueue: `sync` must be a function');
  }
  const storageKey = STORAGE_PREFIX + name;

  // ---- state ----
  let listeners = new Set();
  let status = isOnline() ? QUEUE_STATUS.ONLINE_SYNCED : QUEUE_STATUS.OFFLINE;
  let flushingPromise = null;

  function emit() {
    const snapshot = { status, size: peek().length };
    listeners.forEach((l) => { try { l(snapshot); } catch (_) {} });
  }

  function setStatus(next) {
    if (status !== next) { status = next; emit(); }
    else { emit(); }
  }

  function peek() { return safeRead(storageKey); }

  function persist(arr) { safeWrite(storageKey, arr); }

  function pushEntry(payload) {
    const arr = peek();
    // FIFO drop when over cap so localStorage stays small.
    while (arr.length >= maxSize) {
      // eslint-disable-next-line no-console
      console.warn(`[localFirstQueue:${name}] capacity reached, dropping oldest entry`);
      arr.shift();
    }
    // Optional dedupe — when allowDedupeKey is provided, replace any prior
    // entry that shares the same key value. Used for "today's check-in" so
    // re-submitting on the same day doesn't queue duplicates.
    let next = arr;
    if (allowDedupeKey && payload && payload[allowDedupeKey] != null) {
      next = arr.filter((e) => e?.payload?.[allowDedupeKey] !== payload[allowDedupeKey]);
    }
    next.push({
      schema: 1,
      clientId: generateClientId(),
      queuedAt: new Date().toISOString(),
      payload,
    });
    persist(next);
  }

  /**
   * Try to ship a single entry. Returns true if the backend accepted it.
   * Any thrown error is treated as a failure — caller decides whether to
   * re-queue or surface to the user.
   */
  async function attemptSync(entry) {
    try {
      await sync(entry.payload, { clientId: entry.clientId, queuedAt: entry.queuedAt });
      return true;
    } catch (_err) {
      return false;
    }
  }

  /**
   * Flush attempts to drain the queue. Concurrent flushes coalesce onto a
   * single promise so we never fire duplicate requests for the same entry.
   */
  async function flush() {
    if (flushingPromise) return flushingPromise;
    if (!isOnline()) { setStatus(QUEUE_STATUS.OFFLINE); return false; }
    const arr = peek();
    if (arr.length === 0) { setStatus(QUEUE_STATUS.ONLINE_SYNCED); return true; }

    setStatus(QUEUE_STATUS.FLUSHING);
    flushingPromise = (async () => {
      const remaining = [];
      let anyFailed = false;
      for (const entry of arr) {
        // Stop early if we lose connectivity mid-flush — re-queue what's left.
        if (!isOnline()) { remaining.push(entry); continue; }
        const ok = await attemptSync(entry);
        if (!ok) { anyFailed = true; remaining.push(entry); }
      }
      persist(remaining);
      if (remaining.length === 0) setStatus(QUEUE_STATUS.ONLINE_SYNCED);
      else if (!isOnline()) setStatus(QUEUE_STATUS.OFFLINE);
      else if (anyFailed) setStatus(QUEUE_STATUS.SYNC_PAUSED);
      else setStatus(QUEUE_STATUS.QUEUED);
      return remaining.length === 0;
    })().finally(() => { flushingPromise = null; });
    return flushingPromise;
  }

  /**
   * Primary entry point for callers: tries to sync immediately; on failure
   * (offline OR thrown error) parks the payload locally and updates status.
   *
   * Returns an object the caller can use to display the right toast:
   *   { ok: boolean, queued: boolean, status: QUEUE_STATUS }
   */
  async function enqueueAndSync(payload) {
    if (!isOnline()) {
      pushEntry(payload);
      setStatus(QUEUE_STATUS.QUEUED);
      return { ok: false, queued: true, status };
    }
    try {
      await sync(payload, { clientId: generateClientId(), queuedAt: new Date().toISOString() });
      // Opportunistically drain anything that might have piled up earlier.
      if (peek().length > 0) { void flush(); }
      else setStatus(QUEUE_STATUS.ONLINE_SYNCED);
      return { ok: true, queued: false, status: QUEUE_STATUS.ONLINE_SYNCED };
    } catch (_err) {
      pushEntry(payload);
      setStatus(QUEUE_STATUS.SYNC_PAUSED);
      return { ok: false, queued: true, status };
    }
  }

  function subscribe(listener) {
    listeners.add(listener);
    // Fire once with current snapshot so consumers can render immediately.
    try { listener({ status, size: peek().length }); } catch (_) {}
    return () => listeners.delete(listener);
  }

  // Hook into global online/offline so flush happens automatically.
  ensureGlobalOnlineWired();
  globalOnlineListeners.add((online) => {
    if (online) { void flush(); }
    else setStatus(QUEUE_STATUS.OFFLINE);
  });

  return {
    name,
    enqueueAndSync,
    flush,
    peek,
    size: () => peek().length,
    clear: () => { persist([]); setStatus(isOnline() ? QUEUE_STATUS.ONLINE_SYNCED : QUEUE_STATUS.OFFLINE); },
    subscribe,
    getStatus: () => status,
  };
}

/**
 * Useful for tests / debug screens — returns every queue currently persisted
 * regardless of whether the producing module has been imported in this
 * session. We don't expose it in the UI yet but it's a one-liner for a
 * future "stuck syncs" diagnostic.
 */
export function listAllQueues() {
  if (typeof localStorage === 'undefined') return [];
  const out = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(STORAGE_PREFIX)) {
      out.push({ name: k.slice(STORAGE_PREFIX.length), entries: safeRead(k) });
    }
  }
  return out;
}
